import React, { useEffect, useRef } from 'react';

import { observer } from 'mobx-react';
import { ImperativePanelGroupHandle, PanelGroup } from '@bringg-frontend/resizable-panel';
import classNames from 'classnames';
import { DetailedRun, DetailsColumns } from '@bringg-frontend/monitoring';
import { hasFeatureFlag } from '@bringg-frontend/global-stores';

import { useStores } from 'bringg-web/recipes';
import DetailsNavBar from './details-nav-ber/details-nav-bar';
import { useColumns } from './hooks/use-columns';
import { columnsProvider } from '../utils/details-columns-provider';
import useRouteMonitoringStore from '../stores/helpers/use-route-monitoring-store';
import { getRowsCountWithBottomExtraSpace } from '../utils/rows-provider';

import styles from './details-table.module.scss';

export const DETAILS_GROUP_ID = 'routes-details';
export const DETAILS_PANEL_SELECTOR = '[data-panel-group-id="routes-details"]';
export const DETAILS_PANEL_SIZES_KEY = 'details-panel-group';

interface Props {
	onColumnsChange: (sizeRatioChange: number) => void;
	isScrolled: boolean;
}

const DetailsTable = ({ onColumnsChange, isScrolled }: Props) => {
	const {
		usersStore: { currentUser }
	} = useStores();
	const columnConfigEnabled = hasFeatureFlag(currentUser, 'routes_monitoring_columns_config_enabled');

	const { routesMonitoringStore } = useRouteMonitoringStore();

	const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);
	const columnsLayoutMapRef = useRef<Map<string, number>>(new Map());

	const { columnsToView, columnsConfig, onSelectedColumnsChange, getDefaultConfig } = useColumns(
		DETAILS_GROUP_ID,
		columnsLayoutMapRef.current,
		onColumnsChange
	);

	useEffect(() => {
		return () => {
			columnsProvider.saveUserColumnSizes.cancel();
		};
	}, []);

	const onLayout = (sizes: number[]) => {
		const layoutMap = columnsLayoutMapRef.current;
		layoutMap.clear();

		columnsToView.forEach((item, index) => {
			const size = sizes[index];
			layoutMap.set(item.id, size);
		});

		columnConfigEnabled && columnsProvider.saveUserColumnSizes(DETAILS_PANEL_SIZES_KEY, sizes);
	};

	useEffect(() => {
		const layoutMap = columnsLayoutMapRef.current;
		if (layoutMap.size === 0) return;

		if (columnsToView.length === layoutMap.size) {
			const panelGroup = panelGroupRef.current;

			panelGroup.setLayout(columnsToView.map(column => column.size));
		}
	}, [columnsToView]);

	return (
		<div className={styles.detailsTable} data-test-id="details-table">
			<DetailsNavBar
				columnsConfigProps={{
					columnsConfig,
					onSelectedColumnsChange,
					getDefaultColumns: getDefaultConfig
				}}
			/>
			<PanelGroup
				ref={panelGroupRef}
				direction="horizontal"
				disablePointerEventsDuringResize={true}
				className={styles.panelGroup}
				onLayout={onLayout}
				id={DETAILS_GROUP_ID}
			>
				<DetailsColumns<DetailedRun>
					groupId={DETAILS_GROUP_ID}
					columns={columnsToView}
					data={routesMonitoringStore.detailedRuns}
					rowCount={getRowsCountWithBottomExtraSpace(routesMonitoringStore.detailedRuns.length)}
				/>
			</PanelGroup>
			<div className={classNames(styles.shadow, { [styles.scroll]: isScrolled })} />
			<div className={styles.footer} />
		</div>
	);
};

export default observer(DetailsTable);
